import { getDisplayName } from "./translations.js";
import { iconImgHtml } from "./icon_sheets.js";
import { POTION_COLORS } from './potion_config.js';
import { getPerkDeck, getTemplePerks, rerollTemplePerks, pickupPerk, getGamblePerks, getAlwaysCasts, PERKS } from "./perks.js";

function capitalize(str) {
    if (typeof str !== 'string' || str.length === 0) {
        return str;
    }
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

// Helper to grab the hex color, strip the Noita ARGB alpha, and add the '#'
function getColorStyle(material) {
    const rawHex = POTION_COLORS[material];
    if (rawHex) {
        // Remove the first 2 characters (Alpha) and prepend '#'
        return `color: #${rawHex.slice(2)};`;
    }
    return 'color: #e0e0e0;'; // Fallback neutral color
}

/**
 * Renders the fungal shifts data to the DOM.
 * @param {Array} shiftsData - The array of shift objects.
 */
export function renderFungalShifts(shiftsData) {
    const listElement = document.getElementById('shifts-list');
    listElement.innerHTML = ''; 

    shiftsData.forEach((shift, index) => {
        const li = document.createElement('li');
        li.className = 'shift-item';

        let filteredFrom = shift.fromMaterials.filter(mat => !mat.endsWith('_static'));
        if (filteredFrom.length === 0) {
            filteredFrom = shift.fromMaterials;
        }

        // 1. Format the 'From' materials with inline colors
        const fromMaterialsText = filteredFrom
            .map(mat => `<span class="material-hover" title="${mat}" style="${getColorStyle(mat)}">${capitalize(getDisplayName(mat))}</span>`)
            .join('<span class="comma">, </span><br>');

        const fromHtml = `
            <span class="material-from">${fromMaterialsText}</span>
            ${shift.flaskFrom ? '<span class="flask-tag">Held</span>' : ''}
        `;

        // 2. Format the 'To' material with inline color
        const toHtml = `
            <span class="material-to">
                <span class="material-hover" title="${shift.toMaterial}" style="${getColorStyle(shift.toMaterial)}">${capitalize(getDisplayName(shift.toMaterial))}</span>
            </span>
            ${shift.flaskTo ? '<span class="flask-tag">Held</span>' : ''}
        `;

        // 3. Handle the special gold/grass shifts with inline colors
        let specialHtml = '';
        if (shift.goldToX !== null || shift.grassToX !== null) {
            const goldLucky = (shift.goldToX === "gold") ? " lucky-perk" : "";
            const goldText = shift.goldToX !== null 
                ? `<span class="material-hover" title="gold" style="${getColorStyle('gold')}">Gold</span>? <span class="arrow">➔</span> <span class="material-hover${goldLucky}" title="${shift.goldToX}" style="${getColorStyle(shift.goldToX)}">${capitalize(getDisplayName(shift.goldToX))}</span>`
                : '';
            const grassLucky = (shift.grassToX === "grass_holy") ? " lucky-perk" : "";
            const grassText = shift.grassToX !== null 
                ? `<span class="material-hover" title="grass_holy" style="${getColorStyle('grass_holy')}">Divine Ground</span>? <span class="arrow">➔</span> <span class="material-hover${grassLucky}" title="${shift.grassToX}" style="${getColorStyle(shift.grassToX)}">${capitalize(getDisplayName(shift.grassToX))}</span>` 
                : '';
            
            const combinedSpecial = [goldText, grassText].filter(Boolean).join('<br>');
            specialHtml = `<div class="special-shifts">${combinedSpecial}</div>`;
        }

        // 4. Assemble the row
        li.innerHTML = `
            <div class="shift-row">
                <span class="shift-index">#${index + 1}</span>
                ${fromHtml}
            </div>
            <div class="shift-row indented-row">
                <span class="arrow">➔</span>
                ${toHtml}
            </div>
            ${specialHtml}
        `;

        listElement.appendChild(li);
    });
}

/**
 * Renders the secret alchemy recipes to the DOM.
 * @param {Object} alchemyMaterials - The object containing livelyConcoction and alchemicPrecursor arrays.
 */
export function renderAlchemyRecipes(alchemyMaterials) {
    const listElement = document.getElementById('alchemy-list');
    listElement.innerHTML = ''; 

    // Define the recipes with their target outputs
    const recipes = [
        {
            name: "Lively Concoction",
            inputs: alchemyMaterials.livelyConcoction,
            output: "magic_liquid_hp_regeneration_unstable"
        },
        {
            name: "Alchemic Precursor",
            inputs: alchemyMaterials.alchemicPrecursor,
            output: "midas_precursor"
        }
    ];

    recipes.forEach(recipe => {
        const li = document.createElement('li');
        li.className = 'shift-item'; // Reuse the shift item styling

        // 1. Format the input materials separated by '+'
        const inputHtml = recipe.inputs
            .map(mat => {
                return `<span class="material-hover" title="${mat}" style="${getColorStyle(mat)};">${capitalize(getDisplayName(mat))}</span>`;
            })
            .join('<br><span class="plus"> + </span>');

        // 2. Format the output material
        const outputHtml = `
            <span class="material-hover" title="${recipe.output}" style="${getColorStyle(recipe.output)};">
                ${capitalize(getDisplayName(recipe.output))}
            </span>
        `;

        // 3. Assemble the row (reusing shift layout classes)
        li.innerHTML = `
            <div class="shift-row">
                <span class="material-from">${inputHtml}</span>
            </div>
            <div class="shift-row indented-row">
                <span class="arrow">➔</span>
                <span class="material-to">${outputHtml}</span>
            </div>
        `;

        listElement.appendChild(li);
    });
}

// Internal state
let pwRerolls = { 0: [0, 0, 0, 0, 0, 0, 0] }; 
let uiSelectedPerks = {}; 

let currentSeed = 1;
let currentNg = 0;
let currentPwIndex = 0; // 0 = Main, negative = West, positive = East
let perkPickups = {}; 
let gameMode = 'normal';
let perkLotteryCount = 0;
let showAllAlwaysCasts = false;
let resolvedPerkPickups = {};
let showRerollDeck = false;

function notifyGenerationPerkChanges(perkPickups) {
    document.dispatchEvent(new CustomEvent('perk-simulation-changed', {
        detail: {
            noMoreShuffle: Boolean(perkPickups.NO_MORE_SHUFFLE),
            extraShopItems: perkPickups.EXTRA_SHOP_ITEM || 0,
        }
    }));
}

function formatPerkName(id) {
    if (!id) return '';
    if (typeof id === 'object' && id.id) id = id.id; // Extract from spell object
    else if (typeof id !== 'string') id = String(id);
    
    return id.split('_')
             .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
             .join(' ');
}

function getPerkDisplayName(id) {
    const perkData = PERKS.find(p => p.id === id);
    // Return the official name if found, otherwise fallback to the old formatting
    return (perkData && perkData.name) ? perkData.name : formatPerkName(id);
}

function getTempleCount(ng, pwIndex) {
    if (ng > 0) return pwIndex === 0 ? 5 : 4;
    return pwIndex === 0 ? 7 : 6;
}

function getRemainingRerollDeck(rerollIndex, perkPickups) {
    const perkDeck = getPerkDeck(currentSeed, currentNg, perkPickups, [], gameMode);
    const remainingCount = perkDeck.filter(Boolean).length;
    if (remainingCount === 0) return [];

    const remainingPerks = [];
    let index = rerollIndex ?? perkDeck.length - 1;
    while (remainingPerks.length < remainingCount) {
        if (perkDeck[index]) remainingPerks.push(perkDeck[index]);
        index = (index - 1 + perkDeck.length) % perkDeck.length;
    }
    return remainingPerks;
}

/**
 * Recalculates all perks using a Two-Pass architecture:
 * Pass 1: Simulates state, cleans up orphans (strict index matching), and finalizing global counters.
 * Pass 2: Renders the DOM with accurate global data.
 */
export function renderPerkUI() {
    const listElement = document.getElementById('temple-list');
    if (!listElement) return;

    // Bind static header controls
    const pwLabel = document.getElementById('pw-label');
    const prevBtn = document.getElementById('prev-pw');
    const nextBtn = document.getElementById('next-pw');
    const lotteryInput = document.getElementById('lottery-count');
    const acCheckbox = document.getElementById('show-always-casts');
    const resetBtn = document.getElementById('reset-btn');
    const rerollDeckButton = document.getElementById('reroll-deck-toggle');
    const rerollDeckList = document.getElementById('reroll-deck-list');

    if (pwLabel) {
        if (currentPwIndex === 0) pwLabel.textContent = "Main World";
        else if (currentPwIndex > 0) pwLabel.textContent = "East " + currentPwIndex;
        else pwLabel.textContent = "West " + Math.abs(currentPwIndex);
    }
    
    if (prevBtn) prevBtn.onclick = () => { currentPwIndex--; renderPerkUI(); };
    if (nextBtn) nextBtn.onclick = () => { currentPwIndex++; renderPerkUI(); };
    if (resetBtn) resetBtn.onclick = () => { 
        pwRerolls = {}; 
        uiSelectedPerks = {}; 
        perkLotteryCount = 0;
        if (lotteryInput) lotteryInput.value = 0;
        currentPwIndex = 0;
        renderPerkUI(); 
        notifyGenerationPerkChanges(perkPickups);
    };

    if (lotteryInput) {
        lotteryInput.onchange = (e) => {
            perkLotteryCount = Math.min(6, Math.max(0, parseInt(e.target.value) || 0));
            renderPerkUI();
        };
    }

    if (acCheckbox) {
        acCheckbox.checked = showAllAlwaysCasts;
        acCheckbox.onchange = (e) => {
            showAllAlwaysCasts = e.target.checked;
            renderPerkUI();
        };
    }

    let pIdx = 0;
    let rIdx = null;
    let accumulatedPickups = JSON.parse(JSON.stringify(perkPickups));

    let path = [0];
    if (currentPwIndex > 0) {
        for (let i = 1; i <= currentPwIndex; i++) path.push(i);
    } else if (currentPwIndex < 0) {
        for (let i = -1; i >= currentPwIndex; i--) path.push(i);
    }

    // --- PASS 1: STATE SIMULATION & CLEANUP ---
    let lotteriesLostInCleanup = 0;
    let templesData = []; // Cache generation results so we don't recalculate in Pass 2

    for (let w of path) {
        let tCount = getTempleCount(currentNg, w);
        if (!pwRerolls[w]) pwRerolls[w] = new Array(tCount).fill(0);
        let rerolls = pwRerolls[w];
        
        for (let t = 0; t < tCount; t++) {
            let baseRoll = getTemplePerks(currentSeed, currentNg, w, t, pIdx, accumulatedPickups, gameMode);
            pIdx = baseRoll.perkIndex;
            let finalPerks = baseRoll.perks;

            let rCount = rerolls[t] || 0;
            for (let r = 0; r < rCount; r++) {
                let reroll = rerollTemplePerks(currentSeed, currentNg, w, t, pIdx, rIdx, accumulatedPickups, gameMode);
                rIdx = reroll.rerollIndex;
                finalPerks = reroll.perks;
            }

            finalPerks.forEach(p => {
                if (p.perk === 'GAMBLE') {
                    p.hypotheticalGamble = getGamblePerks(currentSeed, currentNg, pIdx, accumulatedPickups, gameMode);
                }
            });

            const selectionKey = `${w}-${t}`;
            let selectedInTemple = uiSelectedPerks[selectionKey] || [];
            
            const lotteriesBefore = selectedInTemple.filter(s => s.id === 'PERKS_LOTTERY').length;
            
            // STRICT MATCH: Must match the exact perk ID at the exact physical slot index
            selectedInTemple = selectedInTemple.filter(s => {
                return finalPerks[s.i] && finalPerks[s.i].perk === s.id;
            });
            
            const lotteriesAfter = selectedInTemple.filter(s => s.id === 'PERKS_LOTTERY').length;
            if (lotteriesBefore > lotteriesAfter) {
                lotteriesLostInCleanup += (lotteriesBefore - lotteriesAfter);
            }

            uiSelectedPerks[selectionKey] = selectedInTemple;

            finalPerks.forEach((p, i) => p.originalIndex = i);

            templesData.push({ w, t, finalPerks, rCount, selectedInTemple });

            selectedInTemple.forEach(s => {
                if (s.id === 'GAMBLE') {
                    const gambleResult = getGamblePerks(currentSeed, currentNg, pIdx, accumulatedPickups, gameMode);
                    pIdx = gambleResult.perkIndex; 
                    gambleResult.perks.forEach(gp => {
                        accumulatedPickups = pickupPerk(gp, accumulatedPickups);
                    });
                } else {
                    accumulatedPickups = pickupPerk(s.id, accumulatedPickups);
                }
            });
        }
    }

    // Finalize global variables BEFORE building any DOM
    if (lotteriesLostInCleanup > 0) {
        perkLotteryCount = Math.max(0, perkLotteryCount - lotteriesLostInCleanup);
        if (lotteryInput) lotteryInput.value = perkLotteryCount;
    }
    resolvedPerkPickups = accumulatedPickups;
    const remainingRerollDeck = getRemainingRerollDeck(rIdx, resolvedPerkPickups);
    if (rerollDeckButton) {
        rerollDeckButton.textContent = `${showRerollDeck ? 'Hide' : 'Show'} reroll perk deck (${remainingRerollDeck.length})`;
        rerollDeckButton.onclick = () => {
            showRerollDeck = !showRerollDeck;
            renderPerkUI();
        };
    }
    if (rerollDeckList) {
        rerollDeckList.hidden = !showRerollDeck;
        rerollDeckList.innerHTML = remainingRerollDeck.map(perkId => `
            <div class="reroll-deck-card" title="${getPerkDisplayName(perkId)}">
                ${iconImgHtml('perk_sprites', perkId.toLowerCase(), `class="perk-icon" alt="${getPerkDisplayName(perkId)}"`)}
            </div>
        `).join('');
    }

    // --- PASS 2: RENDER DOM ---
    listElement.innerHTML = '';
    
    for (let data of templesData) {
        if (data.w === currentPwIndex) {
            const row = document.createElement('div');
            row.className = 'temple-row';

            const displayPerks = data.finalPerks.slice();

            const cardsHtml = displayPerks.map(p => {
                const luckyIndex = Math.min(perkLotteryCount, p.luckyStates.length - 1);
                const isLucky = p.luckyStates[luckyIndex];
                const luckyClass = isLucky ? 'lucky-perk' : '';
                
                const isSelected = data.selectedInTemple.some(s => s.i === p.originalIndex && s.id === p.perk);
                const selectedClass = isSelected ? 'selected-perk' : '';
                
                let titleText = `${getPerkDisplayName(p.perk)}${isLucky ? ' (Lucky)' : ''}`;
                let altText = `${getPerkDisplayName(p.perk)}${isLucky ? ' (Lucky)' : ''}`;

                let subIconsHtml = '';
                
                // Flag to determine if we need to render the spell card at the bottom
                let needsAcSpell = showAllAlwaysCasts || p.perk === 'ALWAYS_CAST';

                // 1. Build the Gamble Preview (and check if it rolled Always Cast)
                if (p.perk === 'GAMBLE' && p.hypotheticalGamble) {
                    subIconsHtml += `<div class="gamble-preview">
                        ${p.hypotheticalGamble.perks.map(gp => {
                            if (gp === 'ALWAYS_CAST') needsAcSpell = true;
                            return iconImgHtml('perk_sprites', gp.toLowerCase(), `class="gamble-icon" title="${getPerkDisplayName(gp)}"`);
                        }).join('')}
                    </div>`;
                }

                // 2. Render the Always Cast spell exactly once, on its own line below everything else
                if (needsAcSpell) {
                    // Use precalculated AC if available, otherwise calculate it for this position
                    let acSpell = p.alwaysCast || getAlwaysCasts(currentSeed, currentNg, p.x, p.y);
                    if (acSpell) {
                        const spellName = capitalize(getDisplayName(acSpell));
                        titleText += `\nSpell: ${spellName}`;
                        altText += ` (Spell: ${spellName})`;
                        
                        // Creating a new .gamble-preview div forces it to stack below the gamble perks
                        subIconsHtml += `
                            <div class="gamble-preview">
                                ${iconImgHtml('spell_sprites', (acSpell.id ?? acSpell).toLowerCase(), `class="gamble-icon" title="Always Cast: ${spellName}"`)}
                            </div>
                        `;
                    }
                }

                // Add data-index utilizing originalIndex for strict slot tracking
                return `
                    <div class="perk-card ${selectedClass}" data-temple="${data.t}" data-perk="${p.perk}" data-index="${p.originalIndex}">
                        ${iconImgHtml('perk_sprites', p.perk.toLowerCase(), `class="perk-icon ${luckyClass}" title="${titleText}" alt="${altText}"`)}
                        ${subIconsHtml}
                    </div>
                `;
            }).join('');

            row.innerHTML = `
                <div class="perk-cards">
                    ${cardsHtml}
                </div>
                <div class="reroll-controls">
                    <button class="reroll-btn minus-btn" data-temple="${data.t}" ${data.rCount === 0 ? 'disabled' : ''}>-</button>
                    <span class="reroll-count">${data.rCount}</span>
                    <button class="reroll-btn plus-btn" data-temple="${data.t}">+</button>
                </div>
            `;
            listElement.appendChild(row);
        }
    }

    listElement.onclick = (e) => {
        const card = e.target.closest('.perk-card');
        if (card) {
            const t = card.getAttribute('data-temple');
            const perkId = card.getAttribute('data-perk');
            const pIndex = parseInt(card.getAttribute('data-index'), 10);
            const key = `${currentPwIndex}-${t}`;
            
            if (!uiSelectedPerks[key]) uiSelectedPerks[key] = [];
            
            const existingIndex = uiSelectedPerks[key].findIndex(s => s.i === pIndex && s.id === perkId);
            
            if (existingIndex !== -1) {
                uiSelectedPerks[key].splice(existingIndex, 1); 
                if (perkId === 'PERKS_LOTTERY') perkLotteryCount = Math.max(0, perkLotteryCount - 1);
            } else {
                uiSelectedPerks[key].push({ i: pIndex, id: perkId }); 
                if (perkId === 'PERKS_LOTTERY') perkLotteryCount = Math.min(7, perkLotteryCount + 1);
            }
            
            if (lotteryInput) lotteryInput.value = perkLotteryCount;
            renderPerkUI();
            notifyGenerationPerkChanges(resolvedPerkPickups);
            return;
        }

        const minusBtn = e.target.closest('.minus-btn');
        if (minusBtn) {
            const t = parseInt(minusBtn.getAttribute('data-temple'), 10);
            if (pwRerolls[currentPwIndex][t] > 0) {
                pwRerolls[currentPwIndex][t]--;
                renderPerkUI();
            }
            return;
        }

        const plusBtn = e.target.closest('.plus-btn');
        if (plusBtn) {
            const t = parseInt(plusBtn.getAttribute('data-temple'), 10);
            pwRerolls[currentPwIndex][t]++;
            renderPerkUI();
            return;
        }
    };

}

export function getPerkSimulationState() {
    return {
        pwRerolls: structuredClone(pwRerolls),
        selectedPerks: structuredClone(uiSelectedPerks),
        pwIndex: currentPwIndex,
        lotteryCount: perkLotteryCount,
        showAllAlwaysCasts,
        perkPickups: structuredClone(perkPickups),
    };
}

export function importPerkPickups(importedPerks) {
    perkPickups = structuredClone(importedPerks);
    pwRerolls = {};
    uiSelectedPerks = {};
    perkLotteryCount = 0;
    currentPwIndex = 0;
    renderPerkUI();
    notifyGenerationPerkChanges(perkPickups);
}

export function updatePerksState(seed, ng, pwIndex = 0, newPerkPickups = {}, newGameMode = 'normal', lotteryCount = 0, savedState = null, retainTakenPerks = false) {
    currentSeed = seed;
    currentNg = ng;
    gameMode = newGameMode;
    currentPwIndex = retainTakenPerks ? 0 : (savedState?.pwIndex ?? pwIndex);
    perkLotteryCount = savedState?.lotteryCount ?? lotteryCount;
    showAllAlwaysCasts = savedState?.showAllAlwaysCasts ?? false;
    pwRerolls = savedState ? structuredClone(savedState.pwRerolls) : {};
    uiSelectedPerks = savedState && !retainTakenPerks ? structuredClone(savedState.selectedPerks) : {};
    perkPickups = structuredClone(savedState?.perkPickups ?? newPerkPickups);
    if (retainTakenPerks) {
        perkPickups = Object.values(savedState?.selectedPerks ?? {}).flat()
            .reduce((pickups, perk) => pickupPerk(perk.id, pickups), perkPickups);
    }
    
    const lotteryInput = document.getElementById('lottery-count');
    if (lotteryInput) lotteryInput.value = perkLotteryCount;
    
    renderPerkUI();
}